import { Modal, Descriptions, Table, Tag, Divider, Row, Col, Card, Statistic } from 'antd';
import {
    FileTextOutlined,
    UserOutlined,
    CalendarOutlined,
    BankOutlined,
    DollarOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { displayNumbers } from '../../../../variables/numbersFunctions';

export const DetailSinistreModal = ({
    visible,
    onClose,
    sinistre,
    compagniesBeneficiaires = []
}) => {
    if (!sinistre) return null;

    const repartitionColumns = [
        {
            title: 'Compagnie',
            dataIndex: 'beneficiaireNom',
            key: 'beneficiaire',
            render: (nom, record) => (
                <div>
                    <div style={{ fontWeight: 500 }}>{nom}</div>
                    <div style={{ fontSize: 11, color: '#888' }}>{record.beneficiaireCode}</div>
                </div>
            )
        },
        {
            title: 'Taux',
            dataIndex: 'tauxRepartition',
            key: 'taux',
            width: 100,
            align: 'center',
            render: (value) => (
                <Tag color="blue">{(value || 0).toFixed(2)}%</Tag>
            )
        },
        {
            title: 'Montant réparti',
            dataIndex: 'montantSAP',
            key: 'montantSAP',
            width: 150,
            align: 'right',
            render: (value) => (
                <strong style={{ color: value >= 0 ? '#ff4d4f' : '#52c41a' }}>
                    {displayNumbers(Math.abs(value || 0))} F
                </strong>
            )
        }
    ];

    const totalReparti = sinistre.repartition?.reduce((sum, r) => sum + (r.montantSAP || 0), 0) || 0;

    return (
        <Modal
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <FileTextOutlined style={{ fontSize: 20, color: '#722ed1' }} />
                    <span>Détail du sinistre {sinistre.numeroSinistre}</span>
                </div>
            }
            open={visible}
            onCancel={onClose}
            footer={null}
            width={800}
            centered
        >
            {/* Informations principales */}
            <Card size="small" style={{ marginBottom: 16, background: '#fafafa' }}>
                <Row gutter={[16, 16]}>
                    <Col span={12}>
                        <Statistic
                            title="Evaluation sinistre"
                            value={displayNumbers(sinistre.evaluation || 0)}
                            suffix="F"
                            valueStyle={{ color: '#ff4d4f', fontSize: 18 }}
                            prefix={<DollarOutlined />}
                        />
                    </Col>
                    <Col span={12}>
                        <Statistic
                            title="SAP"
                            value={displayNumbers(sinistre.sap || 0)}
                            suffix="F"
                            valueStyle={{ color: '#52c41a', fontSize: 18 }}
                        />
                    </Col>
                </Row>
            </Card>

            {/* Descriptions */}
            <Descriptions
                bordered
                size="small"
                column={2}
                style={{ marginBottom: 16 }}
            >
                <Descriptions.Item label={<><FileTextOutlined /> N° Sinistre</>}>
                    <strong>{sinistre.numeroSinistre || 'N/A'}</strong>
                </Descriptions.Item>
                <Descriptions.Item label={<><CalendarOutlined /> Date de survenance</>}>
                    {sinistre.dateSinistre ? dayjs(sinistre.dateSinistre).format('DD/MM/YYYY') : 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label={<><FileTextOutlined /> N° Police</>}>
                    {sinistre.numeroPolice || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label={<><CalendarOutlined /> Date règlement</>}>
                    {sinistre.dateReglement ? dayjs(sinistre.dateReglement).format('DD/MM/YYYY') : 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label={<><UserOutlined /> Souscripteur</>}>
                    {sinistre.souscripteur || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label={<><UserOutlined /> Bénéficiaire</>}>
                    {sinistre.beneficiaire || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label={<><BankOutlined /> Compagnie cédante</>}>
                    <Tag color="blue">{sinistre.compagnieCedanteCode}</Tag>
                    {sinistre.compagnieCedanteNom}
                </Descriptions.Item>
                <Descriptions.Item label={<><FileTextOutlined /> Catégorie</>}>
                    <Tag color="purple">{sinistre.categorieCode}</Tag>
                    {sinistre.categorieLibelle}
                </Descriptions.Item>
            </Descriptions>

            {/* Tableau de répartition */}
            <Divider orientation="left">
                <BankOutlined /> Répartition par compagnie
            </Divider>

            {sinistre.repartition && sinistre.repartition.length > 0 ? (
                <>
                    <Table
                        columns={repartitionColumns}
                        dataSource={sinistre.repartition}
                        rowKey="beneficiaireCode"
                        pagination={false}
                        size="small"
                        bordered
                        summary={() => (
                            <Table.Summary fixed>
                                <Table.Summary.Row style={{ background: '#f5f5f5' }}>
                                    <Table.Summary.Cell index={0} colSpan={2}>
                                        <strong>TOTAL RÉPARTI</strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={1} align="right">
                                        <strong style={{ color: '#722ed1', fontSize: 14 }}>
                                            {displayNumbers(totalReparti)} F
                                        </strong>
                                    </Table.Summary.Cell>
                                </Table.Summary.Row>
                            </Table.Summary>
                        )}
                    />

                    {/* Visualisation des pourcentages */}
                    <div style={{ marginTop: 16 }}>
                        <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
                            Visualisation de la répartition:
                        </div>
                        <div style={{ display: 'flex', height: 24, borderRadius: 4, overflow: 'hidden' }}>
                            {sinistre.repartition.map((rep, index) => {
                                const colors = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2'];
                                const percentage = totalReparti !== 0
                                    ? Math.abs((rep.montant || 0) / totalReparti) * 100
                                    : 0;
                                return (
                                    <div
                                        key={rep.beneficiaireCode}
                                        style={{
                                            width: `${percentage}%`,
                                            background: colors[index % colors.length],
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: 'white',
                                            fontSize: 10,
                                            fontWeight: 500,
                                            minWidth: percentage > 5 ? 'auto' : 0
                                        }}
                                        title={`${rep.beneficiaireNom}: ${percentage.toFixed(1)}%`}
                                    >
                                        {percentage > 10 ? `${rep.beneficiaireCode}` : ''}
                                    </div>
                                );
                            })}
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 8 }}>
                            {sinistre.repartition.map((rep, index) => {
                                const colors = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2'];
                                return (
                                    <div key={rep.beneficiaireCode} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                        <div style={{
                                            width: 12,
                                            height: 12,
                                            borderRadius: 2,
                                            background: colors[index % colors.length]
                                        }} />
                                        <span style={{ fontSize: 11, color: '#666' }}>
                                            {rep.beneficiaireCode}: {displayNumbers(rep.montantSAP || 0)} F
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </>
            ) : (
                <div style={{
                    textAlign: 'center',
                    padding: 24,
                    color: '#999',
                    background: '#fafafa',
                    borderRadius: 8
                }}>
                    Aucune répartition disponible pour ce sinistre
                </div>
            )}
        </Modal>
    );
};